import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Avatar,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Event as EventIcon,
  Email as EmailIcon,
  VideoCall as VideoCallIcon,
  Phone as PhoneIcon,
  Place as PlaceIcon,
  Close as CloseIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import type { Email, EmailAddress } from '../../stores/mailStore';
import { stringToColor, getInitials } from '../../utils/helpers';

interface MeetingSchedulerDialogProps {
  open: boolean;
  onClose: () => void;
  email: Email | null;
  onScheduleMeeting: (meeting: MeetingData) => void;
}

interface MeetingData {
  title: string;
  description: string;
  date: Date;
  startTime: Date;
  endTime: Date;
  attendees: string[];
  location: string;
  meetingType: 'in-person' | 'video' | 'phone';
  sendInvites: boolean;
  addToCalendar: boolean;
  reminder: number; // minutes before
}

const MeetingSchedulerDialog: React.FC<MeetingSchedulerDialogProps> = ({
  open,
  onClose,
  email,
  onScheduleMeeting,
}) => {
  const [meetingData, setMeetingData] = useState<MeetingData>({
    title: '',
    description: '',
    date: new Date(Date.now() + 86400000), // Tomorrow
    startTime: new Date(new Date().setHours(10, 0, 0, 0)),
    endTime: new Date(new Date().setHours(11, 0, 0, 0)),
    attendees: [],
    location: '',
    meetingType: 'video',
    sendInvites: true,
    addToCalendar: true,
    reminder: 15,
  });

  const [newAttendee, setNewAttendee] = useState('');

  useEffect(() => {
    if (email) {
      const fromAddress = typeof email.from === 'string' 
        ? email.from 
        : email.from?.address || '';
      
      const toAddresses: string[] = [];
      if (email.to) {
        if (typeof email.to === 'string') {
          toAddresses.push(email.to);
        } else if (Array.isArray(email.to)) {
          email.to.forEach((addr: EmailAddress | string) => {
            if (typeof addr === 'string') {
              toAddresses.push(addr);
            } else {
              toAddresses.push(addr.address);
            }
          });
        }
      }

      setMeetingData(prev => ({
        ...prev,
        title: `Meeting: ${email.subject}`,
        description: `Re: ${email.subject}\n\nOriginal email from ${fromAddress}`,
        attendees: [fromAddress, ...toAddresses].filter(Boolean),
      }));
    }
  }, [email]);

  const handleAddAttendee = () => {
    if (newAttendee && !meetingData.attendees.includes(newAttendee)) {
      setMeetingData({
        ...meetingData,
        attendees: [...meetingData.attendees, newAttendee],
      });
      setNewAttendee('');
    }
  };

  const handleRemoveAttendee = (email: string) => {
    setMeetingData({
      ...meetingData,
      attendees: meetingData.attendees.filter(a => a !== email),
    });
  };

  const handleSchedule = () => {
    onScheduleMeeting(meetingData);
    onClose();
  };

  if (!email) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <EventIcon color="primary" />
        Schedule Meeting
      </DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          {/* Email Reference */}
          <Box sx={{ 
            bgcolor: 'action.hover', 
            p: 2, 
            borderRadius: 1, 
            mb: 3,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
          }}>
            <EmailIcon sx={{ color: 'text.secondary', mt: 0.5 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" noWrap>
                {email.subject}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                From: {typeof email.from === 'string' ? email.from : email.from?.address}
              </Typography>
            </Box>
          </Box>

          {/* Meeting Title */}
          <TextField
            fullWidth
            label="Meeting Title"
            value={meetingData.title}
            onChange={(e) => setMeetingData({ ...meetingData, title: e.target.value })}
            sx={{ mb: 2 }}
          />

          {/* Meeting Type */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Meeting Type
            </Typography>
            <ToggleButtonGroup
              value={meetingData.meetingType}
              exclusive
              onChange={(_, value) => value && setMeetingData({ ...meetingData, meetingType: value })}
              size="small"
              fullWidth
            >
              <ToggleButton value="video">
                <VideoCallIcon sx={{ mr: 1 }} /> Video Call
              </ToggleButton>
              <ToggleButton value="phone">
                <PhoneIcon sx={{ mr: 1 }} /> Phone
              </ToggleButton>
              <ToggleButton value="in-person">
                <PlaceIcon sx={{ mr: 1 }} /> In Person
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Date & Time */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <DatePicker
              label="Date"
              value={meetingData.date}
              onChange={(date) => date && setMeetingData({ ...meetingData, date })}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
            <TimePicker
              label="Start"
              value={meetingData.startTime}
              onChange={(time) => time && setMeetingData({ ...meetingData, startTime: time })}
              slotProps={{ textField: { size: 'small', sx: { flex: 1 } } }}
            />
            <TimePicker
              label="End"
              value={meetingData.endTime}
              onChange={(time) => time && setMeetingData({ ...meetingData, endTime: time })}
              slotProps={{ textField: { size: 'small', sx: { flex: 1 } } }}
            />
          </Box>

          {/* Location */}
          {meetingData.meetingType === 'in-person' && (
            <TextField
              fullWidth
              label="Location"
              value={meetingData.location}
              onChange={(e) => setMeetingData({ ...meetingData, location: e.target.value })}
              placeholder="Enter meeting location"
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: <PlaceIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          )}

          {meetingData.meetingType === 'video' && (
            <TextField
              fullWidth
              label="Video Meeting Link"
              value={meetingData.location}
              onChange={(e) => setMeetingData({ ...meetingData, location: e.target.value })}
              placeholder="https://meet.google.com/..."
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: <VideoCallIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          )}

          {/* Attendees */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Attendees ({meetingData.attendees.length})
            </Typography>
            <List dense sx={{ maxHeight: 150, overflow: 'auto', bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
              {meetingData.attendees.map((attendee) => (
                <ListItem
                  key={attendee}
                  secondaryAction={
                    <IconButton edge="end" size="small" onClick={() => handleRemoveAttendee(attendee)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemAvatar>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: stringToColor(attendee), fontSize: '0.75rem' }}>
                      {getInitials(attendee)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    primary={attendee} 
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Add attendee email..."
                value={newAttendee}
                onChange={(e) => setNewAttendee(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddAttendee()}
              />
              <Button variant="outlined" onClick={handleAddAttendee} startIcon={<AddIcon />}>
                Add
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Description */}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description / Agenda"
            value={meetingData.description}
            onChange={(e) => setMeetingData({ ...meetingData, description: e.target.value })}
            sx={{ mb: 2 }}
          />

          {/* Options */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Reminder</InputLabel>
              <Select
                value={meetingData.reminder}
                label="Reminder"
                onChange={(e) => setMeetingData({ ...meetingData, reminder: Number(e.target.value) })}
              >
                <MenuItem value={5}>5 minutes before</MenuItem>
                <MenuItem value={10}>10 minutes before</MenuItem>
                <MenuItem value={15}>15 minutes before</MenuItem>
                <MenuItem value={30}>30 minutes before</MenuItem>
                <MenuItem value={60}>1 hour before</MenuItem>
                <MenuItem value={1440}>1 day before</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={meetingData.sendInvites}
                  onChange={(e) => setMeetingData({ ...meetingData, sendInvites: e.target.checked })}
                />
              }
              label="Send calendar invites to attendees"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={meetingData.addToCalendar}
                  onChange={(e) => setMeetingData({ ...meetingData, addToCalendar: e.target.checked })}
                />
              }
              label="Add to my calendar"
            />
          </Box>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={handleSchedule}
          disabled={!meetingData.title.trim() || meetingData.attendees.length === 0}
          startIcon={<EventIcon />}
        >
          Schedule Meeting
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MeetingSchedulerDialog;
